/**
 * `PolyLayer` / `TrailLayer` (03 §7, §10): many polylines in one updatable mesh with a fixed
 * capacity, per-vertex colour, alpha and width in **stage px** (screen-constant: × uPxScale,
 * extruded in the current camera's viewport px, so it is correct under the ortho camera, insets
 * and every u / r). A custom ribbon mesh + shader instead of GreasedLine: one draw call, no
 * per-frame allocation, alpha per vertex, additive/multiply blending.
 *
 * The vertex shader is a direct transcription of `lineMath.ts#extrudeLineVertex` (unit-tested).
 */
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial';
import { Constants } from '@babylonjs/core/Engines/constants';
import { BoundingInfo } from '@babylonjs/core/Culling/boundingInfo';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Scene } from '@babylonjs/core/scene';
import { GROUP, HUB_UNIFORMS, LAYER, type UniformHub } from './engine.ts';
import { GLSL_HUB } from './shaders/common.ts';
import { LINE_AA_MARGIN_PX, LINE_MITER_LIMIT } from './lineMath.ts';

const LINE_VERTEX = /* glsl */ `
precision highp float;
attribute vec3 position;
attribute vec2 aPrev;
attribute vec2 aNext;
attribute vec4 aColor;
attribute vec2 aSW;      // side (±1), width (stage px)
uniform mat4 viewProjection;
${GLSL_HUB}
varying vec4 vColor;
varying float vEdge;
varying float vHalf;
vec2 toPx(vec2 w) {
  vec4 c = viewProjection * vec4(w.x, 0.0, w.y, 1.0);
  return (c.xy / c.w) * 0.5 * uViewportPx;
}
void main() {
  vec4 cc = viewProjection * vec4(position.x, 0.0, position.z, 1.0);
  vec2 p = toPx(position.xz);
  vec2 a = toPx(aPrev);
  vec2 b = toPx(aNext);
  vec2 d0 = p - a;
  vec2 d1 = b - p;
  float l0 = length(d0);
  float l1 = length(d1);
  vec2 t0 = l0 > 1e-6 ? d0 / l0 : (l1 > 1e-6 ? d1 / l1 : vec2(1.0, 0.0));
  vec2 t1 = l1 > 1e-6 ? d1 / l1 : t0;
  vec2 ts = t0 + t1;
  float tl = length(ts);
  vec2 tg = tl > 1e-6 ? ts / tl : t0;
  vec2 m = vec2(-tg.y, tg.x);
  float cosHalf = max(dot(m, vec2(-t0.y, t0.x)), ${String(1 / LINE_MITER_LIMIT)});
  float halfRaw = 0.5 * aSW.y * uPxScale;
  float halfPx = max(halfRaw, 0.5);
  float len = (halfPx + ${LINE_AA_MARGIN_PX.toFixed(1)}) / cosHalf;
  vec2 off = m * len * aSW.x;
  gl_Position = vec4(cc.xy / cc.w + off / (0.5 * uViewportPx), 0.0, 1.0);
  vEdge = len * aSW.x;
  vHalf = halfPx;
  vColor = vec4(aColor.rgb, aColor.a * min(1.0, 2.0 * halfRaw));
}
`;

const LINE_FRAGMENT = /* glsl */ `
precision highp float;
varying vec4 vColor;
varying float vEdge;
varying float vHalf;
void main() {
  float cov = clamp(vHalf - abs(vEdge) + 0.5, 0.0, 1.0);
  float a = vColor.a * cov;
  if (a <= 0.0) discard;
  gl_FragColor = vec4(vColor.rgb, a);
}
`;

export type LineBlend = 'alpha' | 'add' | 'multiply';

export interface PolyLayerOptions {
  /** max points over all lines (default 8192) */
  capacity?: number;
  layerMask?: number;
  renderingGroupId?: number;
  blend?: LineBlend;
}

export interface LineHost {
  scene: Scene;
  hub: UniformHub;
}

let lineSeq = 0;

export class PolyLayer {
  readonly capacity: number;
  readonly mesh: Mesh;
  readonly material: ShaderMaterial;
  private readonly pos: Float32Array;
  private readonly prev: Float32Array;
  private readonly next: Float32Array;
  private readonly col: Float32Array;
  private readonly sw: Float32Array;
  private readonly idx: Uint32Array;
  private nPts = 0;
  private nIdx = 0;
  private lineStart = -1;
  private idxDirty = true;
  private overflow = false;

  constructor(host: LineHost, opts: PolyLayerOptions = {}) {
    const cap = (this.capacity = opts.capacity ?? 8192);
    const id = lineSeq++;
    const nv = cap * 2;
    this.pos = new Float32Array(nv * 3);
    this.prev = new Float32Array(nv * 2);
    this.next = new Float32Array(nv * 2);
    this.col = new Float32Array(nv * 4);
    this.sw = new Float32Array(nv * 2);
    this.idx = new Uint32Array(cap * 6);
    this.mesh = new Mesh(`lines${String(id)}`, host.scene);
    this.mesh.setVerticesData('position', this.pos, true, 3);
    this.mesh.setVerticesData('aPrev', this.prev, true, 2);
    this.mesh.setVerticesData('aNext', this.next, true, 2);
    this.mesh.setVerticesData('aColor', this.col, true, 4);
    this.mesh.setVerticesData('aSW', this.sw, true, 2);
    this.mesh.setIndices(this.idx, nv, true);
    // The draw range is set per frame via subMeshes[0].indexCount, which makes the sub-mesh
    // non-global; transparent sorting then reads its own bounding info, so give it a fixed one.
    const big = new Vector3(1e5, 1e5, 1e5);
    this.mesh.subMeshes[0]?.setBoundingInfo(new BoundingInfo(big.negate(), big));
    this.mesh.isPickable = false;
    this.mesh.alwaysSelectAsActiveMesh = true;
    this.mesh.layerMask = opts.layerMask ?? LAYER.MAIN;
    this.mesh.renderingGroupId = opts.renderingGroupId ?? GROUP.LINES;
    this.material = new ShaderMaterial(
      `lines${String(id)}`,
      host.scene,
      { vertexSource: LINE_VERTEX, fragmentSource: LINE_FRAGMENT },
      {
        attributes: ['position', 'aPrev', 'aNext', 'aColor', 'aSW'],
        uniforms: ['viewProjection', ...HUB_UNIFORMS],
        needAlphaBlending: true,
      },
    );
    this.material.backFaceCulling = false;
    this.material.disableDepthWrite = true;
    this.setBlend(opts.blend ?? 'alpha');
    host.hub.register(this.material);
    this.mesh.material = this.material;
    this.mesh.isVisible = false;
  }

  setBlend(b: LineBlend): void {
    this.material.alphaMode = b === 'add' ? Constants.ALPHA_ADD : b === 'multiply' ? Constants.ALPHA_MULTIPLY : Constants.ALPHA_COMBINE;
  }

  /** Start a new frame of lines. */
  begin(): void {
    this.nPts = 0;
    this.nIdx = 0;
    this.lineStart = -1;
    this.overflow = false;
  }

  /** True if the last frame ran out of capacity (points were dropped). */
  get overflowed(): boolean {
    return this.overflow;
  }
  get pointCount(): number {
    return this.nPts;
  }

  /** Start a new polyline at world (x, y). Colour 0..255, alpha 0..1, width in stage px. */
  moveTo(x: number, y: number, r: number, g: number, b: number, a: number, widthPx: number): void {
    this.lineStart = -1;
    this.point(x, y, r, g, b, a, widthPx);
  }

  /** Continue the current polyline. */
  lineTo(x: number, y: number, r: number, g: number, b: number, a: number, widthPx: number): void {
    this.point(x, y, r, g, b, a, widthPx);
  }

  private point(x: number, y: number, r: number, g: number, b: number, a: number, w: number): void {
    if (this.nPts >= this.capacity) {
      this.overflow = true;
      return;
    }
    const i = this.nPts++;
    const v0 = i * 2;
    const cont = this.lineStart >= 0;
    if (!cont) this.lineStart = i;
    const px = cont ? (this.pos[(v0 - 2) * 3] ?? x) : x;
    const py = cont ? (this.pos[(v0 - 2) * 3 + 2] ?? y) : y;
    for (let s = 0; s < 2; s++) {
      const v = v0 + s;
      this.pos[v * 3] = x;
      this.pos[v * 3 + 1] = 0;
      this.pos[v * 3 + 2] = y;
      this.prev[v * 2] = px;
      this.prev[v * 2 + 1] = py;
      this.next[v * 2] = x; // patched by the following point
      this.next[v * 2 + 1] = y;
      this.col[v * 4] = r / 255;
      this.col[v * 4 + 1] = g / 255;
      this.col[v * 4 + 2] = b / 255;
      this.col[v * 4 + 3] = a;
      this.sw[v * 2] = s === 0 ? 1 : -1;
      this.sw[v * 2 + 1] = w;
    }
    if (cont) {
      // patch the previous point's "next"
      for (let s = 0; s < 2; s++) {
        const v = v0 - 2 + s;
        this.next[v * 2] = x;
        this.next[v * 2 + 1] = y;
      }
      const a0 = v0 - 2;
      this.writeIdx(a0, v0, a0 + 1, a0 + 1, v0, v0 + 1);
    }
  }

  private writeIdx(a: number, b: number, c: number, d: number, e: number, f: number): void {
    const I = this.idx;
    const n = this.nIdx;
    if (I[n] !== a || I[n + 1] !== b || I[n + 2] !== c || I[n + 3] !== d || I[n + 4] !== e || I[n + 5] !== f) {
      I[n] = a;
      I[n + 1] = b;
      I[n + 2] = c;
      I[n + 3] = d;
      I[n + 4] = e;
      I[n + 5] = f;
      this.idxDirty = true;
    }
    this.nIdx = n + 6;
  }

  /**
   * Append a trail: points 0..n-1 (tail → head), alpha and width interpolated linearly from tail to
   * head. `xs`/`ys` are read from `offset` with `stride`.
   */
  addTrail(
    xs: ArrayLike<number>,
    ys: ArrayLike<number>,
    n: number,
    r: number,
    g: number,
    b: number,
    alphaTail: number,
    alphaHead: number,
    widthTail: number,
    widthHead: number,
    offset = 0,
    stride = 1,
  ): void {
    if (n < 2) return;
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const j = offset + i * stride;
      const x = xs[j] ?? 0;
      const y = ys[j] ?? 0;
      const al = alphaTail + (alphaHead - alphaTail) * t;
      const w = widthTail + (widthHead - widthTail) * t;
      if (i === 0) this.moveTo(x, y, r, g, b, al, w);
      else this.lineTo(x, y, r, g, b, al, w);
    }
  }

  /** Upload this frame's lines. */
  end(): void {
    const m = this.mesh;
    if (this.nIdx === 0) {
      m.isVisible = false;
      return;
    }
    m.updateVerticesData('position', this.pos, false, false);
    m.updateVerticesData('aPrev', this.prev, false, false);
    m.updateVerticesData('aNext', this.next, false, false);
    m.updateVerticesData('aColor', this.col, false, false);
    m.updateVerticesData('aSW', this.sw, false, false);
    if (this.idxDirty) {
      m.updateIndices(this.idx, 0, true);
      this.idxDirty = false;
    }
    const sm = m.subMeshes[0];
    if (sm) {
      sm.indexStart = 0;
      sm.indexCount = this.nIdx;
    }
    m.isVisible = true;
  }

  setEnabled(on: boolean): void {
    this.mesh.setEnabled(on);
  }

  dispose(): void {
    this.mesh.dispose();
    this.material.dispose();
  }
}

/** Trails are polylines with tail→head ramps; the same mesh type. */
export class TrailLayer extends PolyLayer {}

/** Factory handed to styles. */
export class LineFactory {
  constructor(private readonly host: LineHost) {}
  poly(opts?: PolyLayerOptions): PolyLayer {
    return new PolyLayer(this.host, opts);
  }
  trails(opts?: PolyLayerOptions): TrailLayer {
    return new TrailLayer(this.host, opts);
  }
}
